/**
 * Shared Enum Constants
 * 
 * Centralized constants for enums used across the codebase.
 * This is the SINGLE SOURCE OF TRUTH for these values.
 * 
 * Pattern: export const as array, then export type from array.
 */


export const STREAM_STATUSES = [
  'draft',
  'scheduled', 
  'preparing',
  'live',
  'paused',
  'ended',
  'cancelled',
  'archived'
] as const;

export type StreamStatus = typeof STREAM_STATUSES[number];

export const RECORDING_STATUSES = [
  'not_started',
  'pending',
  'recording',
  'processing',
  'completed',
  'failed'
] as const;

export type RecordingStatus = typeof RECORDING_STATUSES[number];

export const NOTIFICATION_TYPES = [
  'stream_live',
  'stream_ended',
  'comment',
  'mention',
  'like',
  'invite',
  'access_granted',
  'access_revoked',
  'system'
] as const;

export type NotificationType = typeof NOTIFICATION_TYPES[number];

export const USER_STATUSES = [
  'active',
  'pending',
  'banned'
] as const;

export type UserStatus = typeof USER_STATUSES[number];

export const INVITE_CODE_STATUSES = [
  'active',
  'used',
  'expired'
] as const;

export type InviteCodeStatus = typeof INVITE_CODE_STATUSES[number];

export const STREAM_VISIBILITY = [
  'public',
  'members',
  'invite_only'
] as const;

export type StreamVisibility = typeof STREAM_VISIBILITY[number];

export const STREAM_CATEGORIES = [
  'gaming',
  'music',
  'education',
  'sports',
  'other'
] as const;

export type StreamCategory = typeof STREAM_CATEGORIES[number];

export const STREAM_QUALITY = [
  '720p',
  '1080p',
  '4K',
  'auto'
] as const;

export type StreamQuality = typeof STREAM_QUALITY[number];

export const AUTH_PROVIDERS = [
  'google',
  'email'
] as const;

export type AuthProvider = typeof AUTH_PROVIDERS[number];

export const AUDIT_SEVERITIES = [
  'low',
  'medium',
  'high',
  'critical'
] as const;

export type AuditSeverity = typeof AUDIT_SEVERITIES[number];

export const AUDIT_ACTIONS = [
  'create',
  'read',
  'update',
  'delete',
  'export',
  'moderate',
  'approve',
  'reject',
  'ban',
  'unban'
] as const;

export type AuditAction = typeof AUDIT_ACTIONS[number];

export const AUDIT_RESOURCE_TYPES = [
  'user',
  'stream',
  'comment',
  'system',
  'setting',
  'qa',
  'poll'
] as const;

export type AuditResourceType = typeof AUDIT_RESOURCE_TYPES[number];

export const SORT_ORDERS = [
  'asc',
  'desc'
] as const;

export type SortOrder = typeof SORT_ORDERS[number];

export const SORT_BY_FIELDS = [
  'createdAt',
  'scheduledStart',
  'viewerCount'
] as const;

export type SortByField = typeof SORT_BY_FIELDS[number];

export const DIGEST_FREQUENCIES = [
  'daily',
  'weekly'
] as const;

export type DigestFrequency = typeof DIGEST_FREQUENCIES[number];

export const STREAM_SOURCE_TYPES = [
  'live',
  'upload'
] as const;

export type StreamSourceType = typeof STREAM_SOURCE_TYPES[number];

export const STREAM_RECORDING_STATUSES = [
  'pending',
  'uploading',
  'processing',
  'completed',
  'failed'
] as const;

export type StreamRecordingStatus = typeof STREAM_RECORDING_STATUSES[number];

export const STREAM_UPLOAD_STATUSES = [
  'uploading',
  'processing',
  'ready',
  'failed'
] as const;

export type StreamUploadStatus = typeof STREAM_UPLOAD_STATUSES[number];

export const MEDIA_STATUSES = [
  'uploading',
  'processing',
  'ready',
  'failed',
  'archived'
] as const;

export type MediaStatus = typeof MEDIA_STATUSES[number];

export const MEDIA_SOURCE_TYPES = [
  'upload',
  'recording',
  'import'
] as const;

export type MediaSourceType = typeof MEDIA_SOURCE_TYPES[number];

export const MEDIA_CATEGORIES = [
  'sermon',
  'music',
  'event',
  'teaching',
  'other'
] as const;

export type MediaCategory = typeof MEDIA_CATEGORIES[number];

export const PUBLISH_MODES = [
  'immediate',
  'scheduled'
] as const;

export type PublishMode = typeof PUBLISH_MODES[number];

export function isValidStreamStatus(value: string): value is StreamStatus {
  return (STREAM_STATUSES as readonly string[]).includes(value);
}

export function isValidNotificationType(value: string): value is NotificationType {
  return (NOTIFICATION_TYPES as readonly string[]).includes(value);
}

export function isValidAuditSeverity(value: string): value is AuditSeverity {
  return (AUDIT_SEVERITIES as readonly string[]).includes(value);
}

export function isValidAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(value);
}
